import { Controller } from "@hotwired/stimulus"

// Saved Views controller for Claims Dashboard.
// Manages the View Selector dropdown, Save View dialog, dirty detection, and Reset.
// Listens for "columns:changed" and "filter:changed" events to recalculate dirty state.
export default class extends Controller {
  static targets = [
    "viewButton", "viewDropdown", "viewName", "dirtyDot",
    "resetButton", "saveButton", "saveDropdown",
    "saveModeTabs", "saveModeUpdate", "saveModeNew",
    "nameInput", "visibilityPrivate", "visibilityShared",
    "saveAction", "viewList"
  ]

  static values = {
    views: Array,
    currentViewId: String,
    savedColumns: Array,
    savedFilters: Object,
    turboFrame: String,
    createUrl: String,
    partnerPath: String
  }

  connect() {
    this.viewDropdownOpen = false
    this.saveDropdownOpen = false
    this.saveMode = "update" // "update" or "new"

    this.handleClickOutside = this.handleClickOutside.bind(this)
    this.handleKeydown = this.handleKeydown.bind(this)
    this.onColumnsChanged = this.onColumnsChanged.bind(this)
    this.onFiltersChanged = this.onFiltersChanged.bind(this)

    document.addEventListener("mousedown", this.handleClickOutside)
    document.addEventListener("keydown", this.handleKeydown)

    // Listen for changes from sibling controllers
    document.addEventListener("claims-columns:changed", this.onColumnsChanged)
    document.addEventListener("claims-filter:changed", this.onFiltersChanged)

    this.updateDirtyState()
  }

  disconnect() {
    document.removeEventListener("mousedown", this.handleClickOutside)
    document.removeEventListener("keydown", this.handleKeydown)
    document.removeEventListener("claims-columns:changed", this.onColumnsChanged)
    document.removeEventListener("claims-filter:changed", this.onFiltersChanged)
  }

  // --- Dirty detection ---

  onColumnsChanged() {
    this.updateDirtyState()
  }

  onFiltersChanged() {
    this.updateDirtyState()
  }

  updateDirtyState() {
    const dirty = this.isDirty()
    if (this.hasDirtyDotTarget) {
      this.dirtyDotTarget.classList.toggle("hidden", !dirty)
    }
    if (this.hasResetButtonTarget) {
      this.resetButtonTarget.classList.toggle("hidden", !dirty)
    }
    if (this.hasSaveButtonTarget) {
      if (dirty) {
        this.saveButtonTarget.classList.remove("text-gray-400", "cursor-not-allowed")
        this.saveButtonTarget.classList.add("text-core-purple-500", "cursor-pointer", "hover:underline")
        this.saveButtonTarget.dataset.disabled = "false"
      } else {
        this.saveButtonTarget.classList.add("text-gray-400", "cursor-not-allowed")
        this.saveButtonTarget.classList.remove("text-core-purple-500", "cursor-pointer", "hover:underline")
        this.saveButtonTarget.dataset.disabled = "true"
      }
    }
  }

  isDirty() {
    const currentColumns = this.getCurrentColumnsFromUrl()
    const currentFilters = this.getCurrentFiltersFromUrl()

    // Column order is meaningful (users can drag-to-reorder), so compare in sequence.
    const savedCols = (this.savedColumnsValue || []).join(",")
    const currCols = currentColumns.join(",")
    if (savedCols !== currCols) return true

    const savedFiltersStr = JSON.stringify(this.sortObject(this.savedFiltersValue || {}))
    const currentFiltersStr = JSON.stringify(this.sortObject(currentFilters))
    return savedFiltersStr !== currentFiltersStr
  }

  sortObject(obj) {
    const sorted = {}
    for (const key of Object.keys(obj).sort()) {
      const val = obj[key]
      sorted[key] = Array.isArray(val) ? val.slice().sort() : val
    }
    return sorted
  }

  getCurrentColumnsFromUrl() {
    const url = new URL(window.location.href)
    const columnsParam = url.searchParams.get("columns")
    if (!columnsParam) return this.savedColumnsValue || []
    return columnsParam.split(",").map(s => s.trim()).filter(Boolean)
  }

  getCurrentFiltersFromUrl() {
    const url = new URL(window.location.href)
    const filters = {}
    // Look for filter-related params from the claims-filter config
    const filterController = document.querySelector("[data-controller*='claims-filter']")
    if (filterController) {
      try {
        const config = JSON.parse(filterController.dataset.claimsFilterConfigValue || "[]")
        for (const f of config) {
          if (f.type === "date_range") {
            const relative = url.searchParams.get(`${f.key}_relative`)
            const from = url.searchParams.get(`${f.key}_from`)
            const to = url.searchParams.get(`${f.key}_to`)
            if (relative) {
              filters[f.key] = [`relative:${relative}`]
            } else if (from || to) {
              filters[f.key] = [`${from || ""}|${to || ""}`]
            }
          } else if (f.type === "currency_range") {
            const exact = url.searchParams.get(`${f.key}_exact`)
            const min = url.searchParams.get(`${f.key}_min`)
            const max = url.searchParams.get(`${f.key}_max`)
            if (exact || min || max) filters[f.key] = [`${exact || ""}|${min || ""}|${max || ""}`]
          } else {
            const val = url.searchParams.get(f.key)
            if (val) filters[f.key] = val.split(",").map(s => s.trim()).filter(Boolean)
          }
        }
      } catch (e) {
        // ignore parse errors
      }
    }
    return filters
  }

  // --- View selector dropdown ---

  toggleViewDropdown() {
    if (this.viewDropdownOpen) {
      this.closeViewDropdown()
    } else {
      this.openViewDropdown()
    }
  }

  openViewDropdown() {
    this.viewDropdownOpen = true
    this.viewDropdownTarget.classList.remove("hidden")
    this.viewButtonTarget.classList.add("underline")
    this.renderViewList()
  }

  closeViewDropdown() {
    this.viewDropdownOpen = false
    this.viewDropdownTarget.classList.add("hidden")
    this.viewButtonTarget.classList.remove("underline")
  }

  renderViewList() {
    const container = this.viewListTarget
    container.innerHTML = ""
    const views = this.viewsValue || []
    const currentId = this.currentViewIdValue

    // Split into my views vs shared
    const myViews = views.filter(v => v.is_mine || v.is_default)
    const sharedViews = views.filter(v => !v.is_mine && !v.is_default && v.is_public)

    if (myViews.length > 0) {
      const header = document.createElement("div")
      header.className = "px-4 pt-3 pb-1 text-[11px] font-semibold text-gray-400 uppercase tracking-wider"
      header.textContent = "My views"
      container.appendChild(header)
      myViews.forEach(v => container.appendChild(this.createViewRow(v, currentId)))
    }

    if (sharedViews.length > 0) {
      const header = document.createElement("div")
      header.className = "px-4 pt-3 pb-1 text-[11px] font-semibold text-gray-400 uppercase tracking-wider"
      header.textContent = "Shared"
      container.appendChild(header)
      sharedViews.forEach(v => container.appendChild(this.createViewRow(v, currentId)))
    }
  }

  createViewRow(view, currentId) {
    const isCurrent = String(view.id) === String(currentId)
    const row = document.createElement("div")
    row.className = `flex items-center gap-2 px-4 py-2 cursor-pointer ${isCurrent ? "bg-core-purple-200" : "hover:bg-support-light-250"}`
    row.dataset.action = "click->claims-views#selectView"
    row.dataset.viewId = view.id
    row.dataset.viewColumns = JSON.stringify(view.columns || [])
    row.dataset.viewFilters = JSON.stringify(view.filters || {})

    // Check icon
    const iconContainer = document.createElement("div")
    iconContainer.className = "w-5 flex-shrink-0"
    if (isCurrent) {
      iconContainer.innerHTML = `<svg width="20" height="20" viewBox="0 0 256 256" class="fill-core-purple-500"><path d="M128,24A104,104,0,1,0,232,128,104.11,104.11,0,0,0,128,24Zm45.66,85.66-56,56a8,8,0,0,1-11.32,0l-24-24a8,8,0,0,1,11.32-11.32L112,148.69l50.34-50.35a8,8,0,0,1,11.32,11.32Z"/></svg>`
    }
    row.appendChild(iconContainer)

    // Name + metadata
    const info = document.createElement("div")
    info.className = "flex-1 min-w-0"
    const name = document.createElement("div")
    name.className = `text-[13px] truncate ${isCurrent ? "font-semibold text-core-purple-500" : "font-medium text-gray-700"}`
    name.textContent = view.name
    info.appendChild(name)

    if (!view.is_default) {
      const meta = document.createElement("div")
      meta.className = "text-[11px] text-gray-400 whitespace-nowrap"
      const visibility = view.is_public ? "Shared" : "Private"
      meta.textContent = view.creator_name ? `${visibility} \u00B7 ${view.creator_name}` : visibility
      info.appendChild(meta)
    }
    row.appendChild(info)

    // Trash icon (only for user's own non-default views)
    if (view.is_mine && !view.is_default) {
      const trash = document.createElement("div")
      trash.className = "text-gray-300 hover:text-alert-red-500 flex-shrink-0 p-1 cursor-pointer"
      trash.dataset.action = "click->claims-views#deleteView"
      trash.dataset.viewId = view.id
      trash.dataset.viewName = view.name
      trash.innerHTML = `<svg width="16" height="16" viewBox="0 0 256 256" fill="currentColor"><path d="M216,48H176V40a24,24,0,0,0-24-24H104A24,24,0,0,0,80,40v8H40a8,8,0,0,0,0,16h8V208a16,16,0,0,0,16,16H192a16,16,0,0,0,16-16V64h8a8,8,0,0,0,0-16ZM96,40a8,8,0,0,1,8-8h48a8,8,0,0,1,8,8v8H96Zm96,168H64V64H192ZM112,104v64a8,8,0,0,1-16,0V104a8,8,0,0,1,16,0Zm48,0v64a8,8,0,0,1-16,0V104a8,8,0,0,1,16,0Z"/></svg>`
      row.appendChild(trash)
    }

    return row
  }

  selectView(event) {
    // Don't select if clicking trash
    if (event.target.closest("[data-action*='deleteView']")) return

    const row = event.currentTarget
    const viewId = row.dataset.viewId
    const columns = JSON.parse(row.dataset.viewColumns || "[]")
    const filters = JSON.parse(row.dataset.viewFilters || "{}")

    this.closeViewDropdown()
    this.applyView(viewId, columns, filters)
  }

  applyView(viewId, columns, filters) {
    const url = new URL(window.location.href)
    const searchParams = url.searchParams

    // Clear existing filter params
    const filterController = document.querySelector("[data-controller*='claims-filter']")
    if (filterController) {
      try {
        const config = JSON.parse(filterController.dataset.claimsFilterConfigValue || "[]")
        for (const f of config) {
          searchParams.delete(f.key)
          searchParams.delete(`${f.key}_from`)
          searchParams.delete(`${f.key}_to`)
          searchParams.delete(`${f.key}_relative`)
          searchParams.delete(`${f.key}_exact`)
          searchParams.delete(`${f.key}_min`)
          searchParams.delete(`${f.key}_max`)
        }
      } catch (e) {
        // ignore
      }
    }

    // Set view param
    if (viewId && viewId !== "default") {
      searchParams.set("view", viewId)
    } else {
      searchParams.delete("view")
    }

    // Set columns
    if (columns.length > 0) {
      searchParams.set("columns", columns.join(","))
    } else {
      searchParams.delete("columns")
    }

    // Set filter params
    for (const [key, values] of Object.entries(filters)) {
      if (!values || values.length === 0) continue
      const filterEl = document.querySelector("[data-controller*='claims-filter']")
      if (filterEl) {
        try {
          const config = JSON.parse(filterEl.dataset.claimsFilterConfigValue || "[]")
          const fConfig = config.find(f => f.key === key)
          if (fConfig && fConfig.type === "date_range") {
            const dateStr = values[0] || ""
            if (this.isRelativeDateRange(dateStr)) {
              const preset = this.relativePresetKey(dateStr)
              const [from, to] = this.resolveRelativeDateRange(preset)
              searchParams.set(`${key}_relative`, preset)
              if (from) searchParams.set(`${key}_from`, from)
              if (to) searchParams.set(`${key}_to`, to)
            } else {
              const [from, to] = dateStr.split("|")
              if (from) searchParams.set(`${key}_from`, from)
              if (to) searchParams.set(`${key}_to`, to)
            }
          } else if (fConfig && fConfig.type === "currency_range") {
            const [exact, min, max] = (values[0] || "").split("|")
            if (exact) searchParams.set(`${key}_exact`, exact)
            if (min) searchParams.set(`${key}_min`, min)
            if (max) searchParams.set(`${key}_max`, max)
          } else {
            searchParams.set(key, values.join(","))
          }
        } catch (e) {
          searchParams.set(key, values.join(","))
        }
      } else {
        searchParams.set(key, values.join(","))
      }
    }

    searchParams.delete("page")

    // Update saved state
    this.currentViewIdValue = String(viewId || "")
    this.savedColumnsValue = columns
    this.savedFiltersValue = filters

    // Update view name display
    const view = (this.viewsValue || []).find(v => String(v.id) === String(viewId))
    if (view && this.hasViewNameTarget) {
      this.viewNameTarget.textContent = view.name
    }

    // The filter bar and column editor live outside the claims_table turbo frame,
    // so they won't re-render when the frame navigates. Push the new view's state
    // into their Stimulus controllers manually so chips and column count stay in sync.
    this.syncFilterController(filters)
    this.syncColumnsController(columns)

    // Navigate
    const frameId = this.turboFrameValue
    if (frameId && document.getElementById(frameId)) {
      // Promote the frame navigation to a Turbo Visit so the URL + history update and the
      // view is restorable on Back. Assigning frame.src directly does NOT consult
      // data-turbo-action in Turbo 8, so we use the public Turbo.visit frame API.
      Turbo.visit(url.toString(), { frame: frameId, action: "replace" })
      this.updateDirtyState()
      return
    }
    window.location.href = url.toString()
  }

  isRelativeDateRange(value) {
    return (value || "").startsWith("relative:")
  }

  relativePresetKey(value) {
    return (value || "").replace(/^relative:/, "")
  }

  resolveRelativeDateRange(preset) {
    const today = this.isoToday()
    if (preset === "today") return [today, today]
    if (preset === "last_7_days") return [this.isoOffset(-7), today]
    if (preset === "last_30_days") return [this.isoOffset(-30), today]
    if (preset === "last_90_days") return [this.isoOffset(-90), today]
    if (preset === "this_month") {
      const d = new Date()
      return [`${d.getFullYear()}-${this.pad(d.getMonth() + 1)}-01`, today]
    }
    if (preset === "this_year") return [`${new Date().getFullYear()}-01-01`, today]
    return ["", ""]
  }

  isoToday() {
    const d = new Date()
    return `${d.getFullYear()}-${this.pad(d.getMonth() + 1)}-${this.pad(d.getDate())}`
  }

  isoOffset(days) {
    const d = new Date()
    d.setDate(d.getDate() + days)
    return `${d.getFullYear()}-${this.pad(d.getMonth() + 1)}-${this.pad(d.getDate())}`
  }

  pad(n) {
    return String(n).padStart(2, "0")
  }

  syncFilterController(filters) {
    const el = document.querySelector("[data-controller*='claims-filter']")
    if (!el) return
    const controller = this.application.getControllerForElementAndIdentifier(el, "claims-filter")
    if (!controller) return
    controller.activeFiltersValue = filters || {}
    if (typeof controller.renderChips === "function") controller.renderChips()
    if (typeof controller.updateButtonLabel === "function") controller.updateButtonLabel()
  }

  syncColumnsController(columns) {
    const el = document.querySelector("[data-controller*='claims-columns']")
    if (!el) return
    const controller = this.application.getControllerForElementAndIdentifier(el, "claims-columns")
    if (!controller) return
    controller.activeColumnsValue = columns || []
    controller.draftColumns = [...(columns || [])]
    if (typeof controller.updateColumnCount === "function") controller.updateColumnCount()
  }

  // --- Reset ---

  reset() {
    this.applyView(
      this.currentViewIdValue,
      this.savedColumnsValue || [],
      this.savedFiltersValue || {}
    )
  }

  // --- Delete ---

  deleteView(event) {
    event.stopPropagation()
    const viewId = event.currentTarget.dataset.viewId
    const viewName = event.currentTarget.dataset.viewName
    if (!confirm(`Delete view "${viewName}"?`)) return

    const form = document.createElement("form")
    form.method = "POST"
    form.action = `${this.partnerPathValue}/claim_views/${viewId}`
    form.style.display = "none"

    const methodInput = document.createElement("input")
    methodInput.type = "hidden"
    methodInput.name = "_method"
    methodInput.value = "delete"
    form.appendChild(methodInput)

    const csrfToken = document.querySelector("meta[name='csrf-token']")?.content
    if (csrfToken) {
      const tokenInput = document.createElement("input")
      tokenInput.type = "hidden"
      tokenInput.name = "authenticity_token"
      tokenInput.value = csrfToken
      form.appendChild(tokenInput)
    }

    document.body.appendChild(form)
    form.submit()
  }

  // --- Save View dropdown ---

  toggleSaveDropdown() {
    if (this.saveDropdownOpen) {
      this.closeSaveDropdown()
    } else {
      this.openSaveDropdown()
    }
  }

  openSaveDropdown() {
    if (this.hasSaveButtonTarget && this.saveButtonTarget.dataset.disabled === "true") return

    // If user is on their own non-default view, immediately update without showing dropdown
    const currentView = (this.viewsValue || []).find(v => String(v.id) === String(this.currentViewIdValue))
    if (currentView && !currentView.is_default && currentView.is_mine) {
      this.saveMode = "update"
      this.saveView()
      return
    }

    // Otherwise show the dropdown for creating a new view
    this.saveDropdownOpen = true
    this.saveDropdownTarget.classList.remove("hidden")
    this.saveMode = "new"
    this.showNewMode()
  }

  closeSaveDropdown() {
    this.saveDropdownOpen = false
    this.saveDropdownTarget.classList.add("hidden")
  }

  showUpdateMode(view) {
    if (this.hasSaveModeTabsTarget) this.saveModeTabsTarget.classList.remove("hidden")
    if (this.hasSaveModeUpdateTarget) {
      this.saveModeUpdateTarget.classList.add("bg-core-purple-200", "text-core-purple-500")
      this.saveModeUpdateTarget.classList.remove("text-gray-500")
      this.saveModeUpdateTarget.textContent = `Update "${view.name}"`
    }
    if (this.hasSaveModeNewTarget) {
      this.saveModeNewTarget.classList.remove("bg-core-purple-200", "text-core-purple-500")
      this.saveModeNewTarget.classList.add("text-gray-500")
    }
    if (this.hasNameInputTarget) this.nameInputTarget.parentElement.classList.add("hidden")
    if (this.hasSaveActionTarget) {
      this.saveActionTarget.textContent = "Update"
      this.saveActionTarget.disabled = false
    }
    this.saveMode = "update"
  }

  showNewMode() {
    const currentView = (this.viewsValue || []).find(v => String(v.id) === String(this.currentViewIdValue))
    if (currentView && !currentView.is_default && currentView.is_mine) {
      if (this.hasSaveModeTabsTarget) this.saveModeTabsTarget.classList.remove("hidden")
      if (this.hasSaveModeUpdateTarget) {
        this.saveModeUpdateTarget.classList.remove("bg-core-purple-200", "text-core-purple-500")
        this.saveModeUpdateTarget.classList.add("text-gray-500")
      }
      if (this.hasSaveModeNewTarget) {
        this.saveModeNewTarget.classList.add("bg-core-purple-200", "text-core-purple-500")
        this.saveModeNewTarget.classList.remove("text-gray-500")
      }
    } else {
      if (this.hasSaveModeTabsTarget) this.saveModeTabsTarget.classList.add("hidden")
    }
    if (this.hasNameInputTarget) this.nameInputTarget.parentElement.classList.remove("hidden")
    if (this.hasSaveActionTarget) {
      this.saveActionTarget.textContent = "Save"
      this.saveActionTarget.disabled = true
    }
    this.saveMode = "new"
  }

  switchToUpdate() {
    const currentView = (this.viewsValue || []).find(v => String(v.id) === String(this.currentViewIdValue))
    if (currentView) this.showUpdateMode(currentView)
  }

  switchToNew() {
    this.showNewMode()
  }

  onNameInput() {
    if (this.saveMode !== "new") return
    if (this.hasSaveActionTarget && this.hasNameInputTarget) {
      this.saveActionTarget.disabled = this.nameInputTarget.value.trim() === ""
    }
  }

  // --- Save action ---

  saveView() {
    const currentColumns = this.getCurrentColumnsFromUrl()
    const currentFilters = this.getCurrentFiltersFromUrl()

    if (this.saveMode === "update") {
      const viewId = this.currentViewIdValue
      // Preserve existing visibility when updating (don't read from radio buttons)
      const currentView = (this.viewsValue || []).find(v => String(v.id) === String(viewId))
      const isPublic = currentView ? currentView.is_public : false

      const form = document.createElement("form")
      form.method = "POST"
      form.action = `${this.partnerPathValue}/claim_views/${viewId}`
      form.style.display = "none"

      this.addHiddenField(form, "_method", "patch")
      this.addHiddenField(form, "columns", JSON.stringify(currentColumns))
      this.addHiddenField(form, "filters", JSON.stringify(currentFilters))
      this.addHiddenField(form, "is_public", isPublic ? "true" : "false")

      const csrfToken = document.querySelector("meta[name='csrf-token']")?.content
      if (csrfToken) this.addHiddenField(form, "authenticity_token", csrfToken)

      document.body.appendChild(form)
      form.submit()
    } else {
      const name = this.hasNameInputTarget ? this.nameInputTarget.value.trim() : ""
      if (!name) return

      const isPublicNew = this.hasVisibilitySharedTarget && this.visibilitySharedTarget.checked

      const form = document.createElement("form")
      form.method = "POST"
      form.action = `${this.partnerPathValue}/claim_views`
      form.style.display = "none"

      this.addHiddenField(form, "name", name)
      this.addHiddenField(form, "columns", JSON.stringify(currentColumns))
      this.addHiddenField(form, "filters", JSON.stringify(currentFilters))
      this.addHiddenField(form, "is_public", isPublicNew ? "true" : "false")

      const csrfToken = document.querySelector("meta[name='csrf-token']")?.content
      if (csrfToken) this.addHiddenField(form, "authenticity_token", csrfToken)

      document.body.appendChild(form)
      form.submit()
    }
  }

  addHiddenField(form, name, value) {
    const input = document.createElement("input")
    input.type = "hidden"
    input.name = name
    input.value = value
    form.appendChild(input)
  }

  // --- Click outside ---

  handleClickOutside(event) {
    if (this.viewDropdownOpen && !this.viewButtonTarget.contains(event.target) && !this.viewDropdownTarget.contains(event.target)) {
      this.closeViewDropdown()
    }
    if (this.saveDropdownOpen && this.hasSaveDropdownTarget) {
      const saveArea = this.saveButtonTarget.parentElement || this.saveButtonTarget
      if (!saveArea.contains(event.target) && !this.saveDropdownTarget.contains(event.target)) {
        this.closeSaveDropdown()
      }
    }
  }

  handleKeydown(event) {
    if (event.key === "Escape") {
      if (this.viewDropdownOpen) this.closeViewDropdown()
      if (this.saveDropdownOpen) this.closeSaveDropdown()
    }
  }
}
